"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("util");
const ksort_1 = require("./helper/ksort");
const substr_count = require('locutus/php/strings/substr_count');
const addcslashes = require('locutus/php/strings/addcslashes');
const sha256 = require('crypto-js/sha256');
const hex = require('crypto-js/enc-hex');
class TwingCompiler {
    constructor(env) {
        this.varNameSalt = 0;
        this.env = env;
    }
    /**
     * Returns the environment instance related to this compiler.
     *
     * @returns TwingEnvironment
     */
    getEnvironment() {
        return this.env;
    }
    getSource() {
        return this.source;
    }
    compile(node, indentation = 0) {
        this.lastLine = null;
        this.source = '';
        this.debugInfo = new Map();
        this.sourceOffset = 0;
        // source code starts at 1 (as we then increment it when we encounter new lines)
        this.sourceLine = 1;
        this.indentation = indentation;
        this.varNameSalt = 0;
        this.subcompile(node);
        return this;
    }
    subcompile(node, raw = true) {
        if (raw === false) {
            this.source += ' '.repeat(this.indentation * 4);
        }
        node.compile(this);
        return this;
    }
    /**
     *
     * @param string
     * @returns
     */
    raw(string) {
        this.source += string;
        return this;
    }
    /**
     * Writes a string to the compiled code by adding indentation.
     *
     * @returns {TwingCompiler}
     */
    write(...strings) {
        for (let string of strings) {
            this.source += ' '.repeat(this.indentation * 4) + string;
        }
        return this;
    }
    /**
     * Adds a quoted string to the compiled code.
     *
     * @param {string} value The string
     *
     * @returns {TwingCompiler}
     */
    string(value) {
        if (!util_1.isNullOrUndefined(value)) {
            if (typeof value === 'string') {
                value = '`' + addcslashes(value, "\0\t\\`").replace(/\${/g, '\\${') + '`';
            }
        }
        else {
            value = '``';
        }
        this.source += value;
        return this;
    }
    repr(value) {
        if (typeof value === 'number') {
            // if (false !== $locale = setlocale(LC_NUMERIC, '0')) {
            //     setlocale(LC_NUMERIC, 'C');
            // }
            this.raw(value);
            // if (false !== $locale) {
            //     setlocale(LC_NUMERIC, $locale);
            // }
        }
        else if (value === null) {
            this.raw('null');
        }
        else if (typeof value === 'boolean') {
            this.raw(value ? 'true' : 'false');
        }
        else if (Array.isArray(value)) {
            this.raw('[');
            let first = true;
            for (let v of value) {
                if (!first) {
                    this.raw(', ');
                }
                first = false;
                this.repr(v);
            }
            this.raw(']');
        }
        else if (value instanceof Map) {
            this.raw('new Map([');
            let first = true;
            for (let [k, v] of value) {
                if (!first) {
                    this.raw(', ');
                }
                first = false;
                this
                    .raw('[')
                    .repr(k)
                    .raw(', ')
                    .repr(v)
                    .raw(']');
            }
            this.raw('])');
        }
        else if (typeof value === 'object') {
            this.raw('{');
            let first = true;
            for (let k in value) {
                if (!first) {
                    this.raw(', ');
                }
                first = false;
                this
                    .raw(`"${k}"`)
                    .raw(': ')
                    .repr(value[k]);
            }
            this.raw('}');
        }
        else if (value === undefined) {
            this.raw('');
        }
        else {
            this.string(value);
        }
        return this;
    }
    /**
     * Adds debugging information.
     *
     * @returns TwingCompiler
     */
    addDebugInfo(node) {
        if (node.getTemplateLine() != this.lastLine) {
            this.write(`// line ${node.getTemplateLine()}, column ${node.getTemplateColumn()}\n`);
            this.sourceLine += substr_count(this.source, "\n", this.sourceOffset);
            this.sourceOffset = this.source.length;
            this.debugInfo.set(this.sourceLine, {
                line: node.getTemplateLine(),
                column: node.getTemplateColumn()
            });
            this.lastLine = node.getTemplateLine();
        }
        return this;
    }
    getDebugInfo() {
        ksort_1.ksort(this.debugInfo);
        return this.debugInfo;
    }
    /**
     * Adds source-map enter call.
     *
     * @returns TwingCompiler
     */
    addSourceMapEnter(node, ctor = null) {
        if (this.getEnvironment().isSourceMap()) {
            this
                .write('this.env.enterSourceMapBlock(')
                .raw(node.getTemplateLine())
                .raw(', ')
                .raw(node.getTemplateColumn())
                .raw(', ')
                .string(node.getType())
                .raw(', ')
                .raw('this.getSourceContext(), ');
            if (ctor) {
                this.raw(ctor);
            }
            else {
                this.raw(`this.extensions.get('TwingExtensionCore').getSourceMapNodeConstructor('${node.getType()}')`);
            }
            this.raw(');\n');
        }
        return this;
    }
    /**
     * Adds source-map leave call.
     *
     * @returns TwingCompiler
     */
    addSourceMapLeave() {
        if (this.getEnvironment().isSourceMap()) {
            this
                .write('this.env.leaveSourceMapBlock();\n');
        }
        return this;
    }
    /**
     * Indents the generated code.
     *
     * @param {number} step The number of indentation to add
     *
     * @returns TwingCompiler
     */
    indent(step = 1) {
        this.indentation += step;
        return this;
    }
    /**
     * Outdents the generated code.
     *
     * @param {number} step The number of indentation to remove
     *
     * @return TwingCompiler
     *
     * @throws Error When trying to outdent too much so the indentation would become negative
     */
    outdent(step = 1) {
        // can't outdent by more steps than the current indentation level
        if (this.indentation < step) {
            throw new Error('Unable to call outdent() as the indentation would become negative.');
        }
        this.indentation -= step;
        return this;
    }
    getVarName(prefix = '__internal_') {
        return `${prefix}${hex.stringify(sha256('TwingCompiler::getVarName' + this.varNameSalt++))}`;
    }
}
exports.TwingCompiler = TwingCompiler;
